import React, { useState, useContext } from 'react';
import moment from 'moment';
import axios from 'axios';

import ReactLoading from 'react-loading';

import OnHoverTooltip from '../../shared/OnHoverTooltip';
import { DataContext } from '../../../../stores/covering-table-store';
import { DataContext as MainDataContext } from '../../../../store';
import WeekHelper from '../../../../functions/WeekHelper';

const CorruptAssignmentRow = ({ empID, assignment: initialAssignment, isLocked, setIsLocked, showErrorMessage }) => {
    // ------- DataContext -------
    const { store, dispatch } = useContext(DataContext);
    const {
        startWeek: filterStartDate,
        endWeek: filterEndDate,
        selectedDate: selectedCoveringDate,
        latestDate: latestCoveringDate,
        coverings: initialCoverings,
        bltIDToNames,
    } = store;
    const filterStartWeek = WeekHelper.GetWeek(filterStartDate);
    const filterEndWeek = WeekHelper.GetWeek(filterEndDate);
    const selectedCoveringDateString = moment(selectedCoveringDate).format('YYYY-MM-DD').toString();
    const latestCoveringDateString = moment(latestCoveringDate).format('YYYY-MM-DD').toString();
    const {
        store: { user },
    } = useContext(MainDataContext);

    // ------- States -------
    const [begWeek, setBegWeek] = useState(initialAssignment.begWek);
    const [endWeek, setEndWeek] = useState(initialAssignment.endWek);
    const [bltID, setBltID] = useState(initialAssignment.bltID);
    const [pct, setPct] = useState(initialAssignment.pct);
    const [dsc, setDsc] = useState(initialAssignment.dsc ?? '');

    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    // ------- Fields -------
    const isAdmin = user.isAdmin;
    const isLatestBlgDate = selectedCoveringDateString === latestCoveringDateString;
    const percents = [25, 50, 75, 100];
    const loaderSize = '25px';

    const isBegWeekInvalid = begWeek.length !== 6 || begWeek.match(/^[0-9]+$/) === null;
    const isEndWeekInvalid = endWeek.length !== 6 || endWeek.match(/^[0-9]+$/) === null;
    const isPctInvalid = !percents.some((p) => p.toString() === pct.toString());
    const hasChanged =
        begWeek !== initialAssignment.begWek || endWeek !== initialAssignment.endWek || pct.toString() !== initialAssignment.pct.toString();

    const isEditable = isAdmin && isLatestBlgDate;
    const isDisabled = !isEditable || isLocked;
    const isSaveDisabled = isDisabled || !hasChanged || isBegWeekInvalid || isEndWeekInvalid;

    // ------- Save handlers -------
    const handleSave = () => {
        setIsLocked(true);
        setIsSaving(true);
        const url = 'api/covering/updateAssignment/';

        const body = {
            empID,
            initial: initialAssignment,
            updated: { dsc: dsc === '' ? null : dsc, bltID, begWek: begWeek, endWek: endWeek, pct: parseInt(pct) },
            filterStartWeek,
            filterEndWeek,
            coveringDate: selectedCoveringDateString,
        };

        axios.post(url, body).then(
            (response) => {
                if (response.data.success) {
                    setIsLocked(false);
                    setIsSaving(false);
                    updateFromResult(response.data);
                    dispatch({ type: 'SHOW_SAVED_TOAST' });
                } else {
                    showErrorMessage(response.data.message);
                    setIsLocked(false);
                    setIsSaving(false);
                }
            },
            (error) => {
                showErrorMessage(
                    'Det gick inte att spara ändringen. Vänligen kontakta alltid@consid.se om problemet kvarstår efter att du uppdaterat sidan.'
                );
                setIsLocked(false);
                setIsSaving(false);
            }
        );
    };

    const handleDelete = () => {
        const url = 'api/covering/deleteAssignment/';
        setIsLocked(true);
        setIsDeleting(true);

        const body = {
            empID,
            deleteAssignment: initialAssignment,
            filterStartWeek,
            filterEndWeek,
            coveringDate: selectedCoveringDateString,
        };

        axios.post(url, body).then(
            (response) => {
                if (response.data.success) {
                    setIsLocked(false);
                    setIsDeleting(false);
                    dispatch({ type: 'SHOW_DELETED_TOAST' });
                    updateFromResult(response.data);
                } else {
                    showErrorMessage(response.data.message);
                    setIsLocked(false);
                    setIsDeleting(false);
                }
            },
            (error) => {
                showErrorMessage(
                    'Det gick inte att spara ändringen. Vänligen kontakta alltid@consid.se om problemet kvarstår efter att du uppdaterat sidan.'
                );
                setIsLocked(false);
                setIsDeleting(false);
            }
        );
    };

    const updateFromResult = (result) => {
        let newCoverings = initialCoverings.map((cov) => {
            if (cov.empID === empID) {
                cov.assignments = result.assignments;
                cov.renderSumAssignments = result.renderSumAssignments;
                cov.coveringRate = result.coveringRate;
            }
            return cov;
        });
        dispatch({
            type: 'UPDATE_COVERINGS',
            coverings: newCoverings,
        });
    };

    // ------- Component -------

    return (
        <tr className='corrupt-assignment-row'>
            <td>
                <div className='input-group week'>
                    <div className='input-group-prepend'>
                        <span className='input-group-text'>v.</span>
                    </div>
                    <input
                        type='text'
                        className={'form-control' + (isBegWeekInvalid ? ' is-invalid' : '')}
                        disabled={isDisabled}
                        value={begWeek}
                        onChange={(e) => setBegWeek(e.target.value)}
                    />
                </div>
            </td>
            <td>
                <div className='input-group week'>
                    <div className='input-group-prepend'>
                        <span className='input-group-text'>v.</span>
                    </div>
                    <input
                        type='text'
                        className={'form-control' + (isEndWeekInvalid ? ' is-invalid' : '')}
                        disabled={isDisabled}
                        value={endWeek}
                        onChange={(e) => setEndWeek(e.target.value)}
                    />
                </div>
            </td>
            <td>
                {Object.keys(bltIDToNames).some((id) => id === initialAssignment.bltID) ? (
                    <select className='form-control blt' disabled={true} value={bltID} onChange={(event) => setBltID(event.target.value)}>
                        {Object.keys(bltIDToNames).map((key) => (
                            <option value={key} key={key}>
                                {bltIDToNames[key]}
                            </option>
                        ))}
                    </select>
                ) : (
                    <input
                        type='text'
                        className='form-control blt'
                        disabled={true}
                        value={bltID}
                        onChange={(e) => setBltID(e.target.value)}
                    />
                )}
            </td>
            <td>
                {percents.some((p) => p === initialAssignment.pct) ? (
                    <select
                        className='form-control pct'
                        value={pct}
                        disabled={isDisabled}
                        onChange={(event) => setPct(parseInt(event.target.value))}>
                        {percents.map((p) => (
                            <option value={p} key={p}>{`${p} %`}</option>
                        ))}
                    </select>
                ) : (
                    <input
                        type='text'
                        className={'form-control pct' + (isPctInvalid ? ' is-invalid' : '')}
                        disabled={isDisabled}
                        value={pct}
                        onChange={(e) => setPct(e.target.value)}
                    />
                )}
            </td>
            <td>
                <input type='text' className='form-control' disabled={true} value={dsc} onChange={(e) => setDsc(e.target.value)} />
            </td>
            {isEditable && (
                <>
                    <td className='buttons'>
                        <OnHoverTooltip isSlimmed={true}>
                            <button className='btn btn-sm btn-success' disabled={isSaveDisabled} onClick={handleSave}>
                                {isSaving ? (
                                    <ReactLoading type={'bars'} className='loader' height={loaderSize} width={loaderSize} color='#F5F5F5' />
                                ) : (
                                    <div className='oi oi-circle-check' />
                                )}
                            </button>
                            <div>Spara</div>
                        </OnHoverTooltip>
                        <OnHoverTooltip isSlimmed={true}>
                            <button className='btn btn-sm btn-danger' disabled={isDisabled} onClick={handleDelete}>
                                {isDeleting ? (
                                    <ReactLoading type={'bars'} className='loader' height={loaderSize} width={loaderSize} color='#F5F5F5' />
                                ) : (
                                    <div className='oi oi-trash' />
                                )}
                            </button>
                            <div>Ta bort</div>
                        </OnHoverTooltip>
                    </td>
                </>
            )}
        </tr>
    );
};

export default CorruptAssignmentRow;
